/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid2";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Typography from "@mui/material/Typography";

import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridTextfield from "../../widgets/GridTextfield";

import {
  valudateTextfield2,
  validateCurrencyField2,
  validateCurrencyField3,
  removeCommaFromCurrency,
  convertLocaleString,
} from "../../../utils/fieldValidator.js";

import { updateOtherPurpose } from "../../../states/slcObjectives.js";

import { calculateInflatedFutureValue } from "../../../utils/formula.js";

export default function OtherPurpose() {
  const otherPurpose = useSelector((state) => state.objectives.wealthaccumulation.otherPurpose);

  const [purpose, setPurpose] = useState(otherPurpose.purpose);
  const [purposeError, setPurposeError] = useState(otherPurpose.purposeError);
  const [targetAmount, setTargetAmount] = useState(otherPurpose.targetAmount);
  const [yearsToTarget, setYearsToTarget] = useState(otherPurpose.yearsToTarget);
  const [resCashAssets, setResCashAssets] = useState(otherPurpose.resCashAssets);
  const [resInvestments, setResInvestments] = useState(otherPurpose.resInvestments);
  const [resRateOfReturn, setResRateOfReturn] = useState(otherPurpose.resRateOfReturn);

  const [fvOfCurrSavAndInv, setFvOfCurrSavAndInv] = useState(otherPurpose.fvOfCurrSavAndInv);
  const [surplusShortfall, setSurplusShortfall] = useState(otherPurpose.surplusShortfall);

  const [deviationReason, setDeviationReason] = useState(otherPurpose.deviationReason);
  const [deviationReasonError, setDeviationReasonError] = useState();

  const dispatch = useDispatch();

  //COMPONENT ID
  let id = "";

  /**==============================
   * EFFECTS
   ==============================*/
  useEffect(calcFvOfCurrentSavingsAndInvestment, [yearsToTarget, resCashAssets, resInvestments, resRateOfReturn]);
  useEffect(calcSurplusOrShortfall, [fvOfCurrSavAndInv, targetAmount]);

  /**==============================
   * FUNCTION
   ==============================*/
  function calcFvOfCurrentSavingsAndInvestment() {
    //CALCULATE FV (Desired Monthly Income)
    let futureValue = calculateInflatedFutureValue(
      Number(removeCommaFromCurrency(resCashAssets)) + Number(removeCommaFromCurrency(resInvestments)),
      Number(resRateOfReturn),
      Number(yearsToTarget)
    );

    //CONVERT INTO COMMA CURRENCY STRING WITH COMMA
    futureValue = convertLocaleString(futureValue, "en-US", 2);

    updateRedux("fvOfCurrSavAndInv", futureValue, updateOtherPurpose);
    setFvOfCurrSavAndInv(futureValue);
  }

  function calcSurplusOrShortfall() {
    let surplusShortfall =
      Number(removeCommaFromCurrency(fvOfCurrSavAndInv)) - Number(removeCommaFromCurrency(targetAmount));

    surplusShortfall = convertLocaleString(surplusShortfall, "en-US", 2);

    updateRedux("surplusShortfall", surplusShortfall, updateOtherPurpose);
    setSurplusShortfall(surplusShortfall);
  }

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, object) {
    let input_data = {
      event: e,
      fieldName: "",
      fieldVariable: "",
      isMandatory: false,
    };

    //TEXTFIELDS
    id = e.target.id;
    validateFields(e, id, input_data);
  }

  function validateFields(e, id, input_data) {
    /**==============================
     * OTHER PURPOSE NEEDS
     ==============================*/
    if (id === "purpose") {
      input_data.fieldName = "Purpose";
      input_data.fieldVariable = purpose;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setPurpose(result.value);
        updateRedux("purpose", result.value, updateOtherPurpose);
      }
      setPurposeError(result.errorMessage);
    }

    if (id === "targetAmount") {
      input_data.fieldName = "Target Amount";
      input_data.fieldVariable = targetAmount;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setTargetAmount(result.value);
        updateRedux("targetAmount", result.value, updateOtherPurpose);
      }
    }

    if (id === "yearsToTarget") {
      input_data.fieldName = "Years To Target Amount";
      input_data.fieldVariable = yearsToTarget;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setYearsToTarget(result.value);
        updateRedux("yearsToTarget", result.value, updateOtherPurpose);
      }
    }

    if (id === "resCashAssets") {
      input_data.fieldName = "Cash Asset";
      input_data.fieldVariable = resCashAssets;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResCashAssets(result.value);
        updateRedux("resCashAssets", result.value, updateOtherPurpose);
      }
    }

    if (id === "resInvestments") {
      input_data.fieldName = "Investment";
      input_data.fieldVariable = resInvestments;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResInvestments(result.value);
        updateRedux("resInvestments", result.value, updateOtherPurpose);
      }
    }

    if (id === "resRateOfReturn") {
      input_data.fieldName = "Rate Of Return";
      input_data.fieldVariable = resRateOfReturn;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResRateOfReturn(result.value);
        updateRedux("resRateOfReturn", result.value, updateOtherPurpose);
      }
    }

    if (id === "deviationReason") {
      input_data.fieldName = "Deviation reason";
      input_data.fieldLength = 1000;
      input_data.fieldVariable = deviationReason;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setDeviationReason(result.value);
        updateRedux("deviationReason", result.value, updateOtherPurpose);
      }

      setDeviationReasonError(result.errorMessage);
    }
  }

  return (
    <>
      <Grid container spacing={2} size={12}>
        <GridSectionHeader
          sectionName="Savings for other Purposes"
          sx={{
            p: 1,
            pl: 1.8,
            mt: 0,
            mb: 1,
            fontSize: 18,
            fontWeight: "bold",
            color: "#FFFFFF", //WHITE
            // color: "#000000", //BLACK
            // backgroundColor: "#d9d9d9",
            backgroundColor: "#10B981",
          }}
        />

        {/*==============================
          PURPOSE
        ==============================*/}
        <Grid size={2}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Purpose
          </Typography>
        </Grid>
        <GridTextfield
          id="purpose"
          key="purpose"
          columnSize="10"
          currState={purpose}
          onChange={handleChange}
          error={purposeError}
          helperText={purposeError}
        />

        {/*==============================
          TARGET AMOUNT
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            (A) Target Amount
          </Typography>
        </Grid>
        <GridTextfield
          id="targetAmount"
          key="targetAmount"
          columnSize="3"
          isCurrency={true}
          currState={targetAmount}
          onChange={handleChange}
        />

        {/*==============================
          YEARS TO TARGET
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Years to target (n)
          </Typography>
        </Grid>
        <GridTextfield
          id="yearsToTarget"
          key="yearsToTarget"
          columnSize="3"
          currState={yearsToTarget}
          onChange={handleChange}
        />

        <Grid size={12}>
          <Typography
            variant="h5"
            sx={{
              pt: 2.0,
              fontWeight: "bold",
              textDecoration: "underline",
            }}
          >
            Existing Resources Allocated for Children’s Education
          </Typography>
        </Grid>

        {/*==============================
        CASH ASSET
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Cash Assets (Savings, Fixed Deposits, etc.) #
          </Typography>
        </Grid>

        <GridTextfield
          id="resCashAssets"
          key="resCashAssets"
          columnSize="3"
          isCurrency={true}
          currState={resCashAssets}
          onChange={handleChange}
          disabled={false}
        />

        {/*==============================
        INVESTMENTS (BONDS, UNIT TRUSTS, SHARES, ETC...)
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Investments (Bonds, Unit Trusts, Shares, etc.) #
          </Typography>
        </Grid>

        <GridTextfield
          id="resInvestments"
          key="resInvestments"
          columnSize="3"
          isCurrency={true}
          currState={resInvestments}
          onChange={handleChange}
          disabled={false}
        />

        {/*==============================
        ESTIMATED RATE OF RETURN
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Estimated Rate Of Return
          </Typography>
        </Grid>

        <GridTextfield
          id="resRateOfReturn"
          key="resRateOfReturn"
          columnSize="3"
          label="Percent"
          currState={resRateOfReturn}
          onChange={handleChange}
          disabled={false}
        />

        {/*==============================
        FUTURE VALUE FO CURRENT SAVINGS AND INVESTMENTS (FV)
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
              fontWeight: "bold",
            }}
          >
            (B) Future Value of Current Savings and Investment (fv)
          </Typography>
        </Grid>

        <GridTextfield
          id="fvOfCurrSavAndInv"
          key="fvOfCurrSavAndInv"
          columnSize="3"
          isCurrency={true}
          currState={fvOfCurrSavAndInv}
          onChange={handleChange}
          disabled={true}
        />
      </Grid>

      {/*==============================
        SURPLUS / SHORTFALL
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
            fontWeight: "bold",
          }}
        >
          (B) - (A) = Surplus / (Shortfall)
        </Typography>
      </Grid>

      <GridTextfield
        id="surplusShortfall"
        key="surplusShortfall"
        columnSize="3"
        isCurrency={true}
        currState={surplusShortfall}
        onChange={handleChange}
        disabled={true}
      />

      <Typography variant="body1">Reason(s) for Deviations:</Typography>
      <TextareaAutosize
        id="deviationReason"
        placeholder="Type here..."
        style={{ width: "100%" }}
        minRows={5}
        onChange={handleChange}
        defaultValue={deviationReason}
      />
      {deviationReasonError && <FormHelperText error={deviationReasonError}>{deviationReasonError}</FormHelperText>}

      <Typography variant="body2">
        # Summation of Cash Assets allocated in needs analysis for retirement, children’s education and/or savings for
        other purposes should not exceed 100% of Total Cash/Near Cash declared in Section 6. If deviate, Representative
        to justify. Representative to justify.
      </Typography>

      <Typography variant="body2">
        Similarly for Investments, summation of Investments allocated in needs analysis for retirement, children’s
        education and/or savings for other purposes should not exceed 100% of Unit Trust + Stocks + Bonds declared in
        Section 6. If deviate, Representative to justify.
      </Typography>
    </>
  );
}
